import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react'
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { fetchArticleById, updateArticle } from '../../api/article';
import { actualitiesLink, adminActualityLink } from '../../constants/endspoints';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { BASE_URL_CATEGORIES, fetchCategories } from '../../api/categories';
import Editor from '../Editor';
import { format } from 'date-fns';

const ActualityEdit = () => {

    const {id:actualityId} = useParams()

    const navigate = useNavigate()
    
    const [title, setTitle] = useState('')
    const [category, setCategory] = useState('')
    const [content, setContent] = useState('')
    const [contentCopy, setContentCopy] = useState('')
    const [contentErr, setContentErr] = useState(false)
    const [clearEditor, setClearEditor] = useState(false)

    const {register,formState: { errors },handleSubmit, reset} = useForm(
        { defaultValues: { title: title, category: category } }
    );

    const queryClient = useQueryClient();

    // Fetch a single actuality by ID
    const { data: singleActuality, isLoading: isFetchingSingle, isError: isErrorSingleData } = useQuery({
        queryKey: ['actuality', actualityId],
        queryFn: () => fetchArticleById(actualityId),
        enabled: !!actualityId,
        retry: false,
        refetchInterval: false,
    });

    useEffect(() => {
        if (singleActuality) {
            reset({
                title: singleActuality?.data?.title,
                category: singleActuality?.data?.Category?.id
            });
            setTitle(singleActuality?.data?.title)
            setCategory(singleActuality?.data?.Category?.id)
            setContent(singleActuality?.data?.content)
            setContentCopy(singleActuality?.data?.content)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singleActuality]);


    // Fetch all categories
    const { data: categoriesData } = useQuery({
        queryKey: ['categories'],
        queryFn: fetchCategories,
    });



    // Update actuality
    const toastAct = useRef(null);
    const updateActuality = useMutation({
        mutationFn: (data)=> updateArticle(data.id, data.formData),
        onMutate: ()=>{
            toastAct.current = toast.loading("Modification en cours...", { 
                position: 'top-right',
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['actualities']);
            toast.dismiss(toastAct.current)
            toastAct.current = toast.success("Actualité modifiée avec succès", {
                type: "success",
                isLoading: false,
                autoClose: 5000,
            });
            navigate(actualitiesLink+"/"+actualityId)
            setClearEditor(true)
        },
        onError: (err) =>{
            toast.dismiss(toastAct.current)
            toastAct.current = toast.error("Une erreur est survenue: "+err.message, {
                isLoading: false,
                hideProgressBar: false,
                closeOnClick: true
            });
        }
    });



    const submit = () =>{
        if (!content) {
            setContentErr(true)
            return
        }
        updateActuality.mutate({id:actualityId, formData:{title:title, content:content, Category:`${BASE_URL_CATEGORIES}/${category}`}})
        setContentErr(false)
    }


    return (
        <>
            <h1 className='fs-3 fw-bold mb-3'> <NavLink className={'fs-3 fw-bold text-white'} to={adminActualityLink}>Actualités</NavLink> / Modifier</h1>


            {/* form */}
            <form onSubmit={handleSubmit(submit)} className='bg-black rounded-3 p-2 shadow'>
                <h2 className='fs-5 fw-bold mb-2'>Modifier une actualité</h2>

                {
                    (isFetchingSingle || isErrorSingleData)?
                    <>
                        <div className='bgcolor-hero-actuality mb-1 placeholder-wave'>
                            <div className='container-xxl h-100'>
                                <div className=' d-flex flex-column justify-content-end align-items-center text-center h-100 w-100 mx-auto' style={{maxWidth:'768px'}}>
                                    <div>
                                        <span className="spinner-border spinner-border-sm" ></span>
                                        <span role="status" className='fs-5'>Chargement...</span>
                                    </div>
                                    <div className=' fs-2 placeholder col-12 bgcolor-primary'></div>
                                </div>
                            </div>
                        </div>
                        <div className='mx-auto placeholder-wave' style={{maxWidth:'768px'}}>
                            <div className='mb-2 placeholder bgcolor-primary' style={{height:'50vh', width:'100%'}}></div>

                            <p className='fs-9s text-opacity-75 lh-lg placeholder col-12 bgcolor-primary'></p>
                            <p className='fs-9s text-opacity-75 lh-lg placeholder col-12 bgcolor-primary'></p>
                            <p className='fs-9s text-opacity-75 lh-lg placeholder col-12 bgcolor-primary'></p>
                            <p className='fs-9s text-opacity-75 lh-lg placeholder col-12 bgcolor-primary'></p>
                            <p className='fs-9s text-opacity-75 lh-lg placeholder col-12 bgcolor-primary'></p>
                            <p className='fs-9s text-opacity-75 lh-lg placeholder col-12 bgcolor-primary'></p>
                            <p className='fs-9s text-opacity-75 lh-lg placeholder col-12 bgcolor-primary'></p>
                            <p className='fs-9s text-opacity-75 lh-lg placeholder col-12 bgcolor-primary'></p>
                            <p className='fs-9s text-opacity-75 lh-lg placeholder col-12 bgcolor-primary'></p>
                            <p className='fs-9s text-opacity-75 lh-lg placeholder col-12 bgcolor-primary'></p>

                            <hr/>

                            <div className=' d-flex justify-content-end'>
                                <span className='fst-italic fs-9s placeholder col-3 bgcolor-primary'></span>
                            </div>

                        </div>
                    </>
                    :singleActuality &&
                    <>
                        <div className=' row justify-content-start align-items-start'>
                            <div className=' col-md-6 col-12'>
                                <div>
                                    <div className='col-12 mt-3'>
                                        <div className='inputBox'>
                                            <input {...register("title", {required: true })} onChange={(e)=>(setTitle(e.target.value))} value={title} type='text' className='w-100 fs-9s' required/>
                                            <span>Titre</span>
                                            <i></i>
                                        </div>
                                        {errors.title && <small className="text-danger fs-7s" role="alert">Titre requis</small>}
                                    </div>

                                    <div className='col-12 mt-3'>
                                        <div className='inputBox w-100'>
                                            <select  {...register("category", {required: true})}  onChange={(e)=>setCategory(e.target.value)} value={category} className='w-100 fs-9s bg-black'>
                                                <option value={""} className='text-capitalize fs-9s' defaultValue> --Selectionnez une catégorie--- </option>
                                                {categoriesData?.data?.length !== 0 ?
                                                    categoriesData?.data?.map((cat, index) => (
                                                        <option key={index} value={cat.id} className='fs-9s'>{cat.name}</option>
                                                    ))
                                                :
                                                <option onChange={(e)=>{setCategory(e.target.value);}} value={null}>--Aucune valeur--</option>
                                                }
                                            </select>
                                            <span>Sélectionner une catégorie</span>
                                            <i></i>
                                        </div>
                                        {errors.category && <small className="text-danger fs-7s" role="alert">Catégorie requise</small>}
                                    </div>

                                    <div className='col-12 mt-3'>
                                        <label className='fs-9s color-primary'>Editeur de contenu</label>
                                        <Editor getData={setContent} data={contentCopy} clearForm={clearEditor}/>
                                        {contentErr && <small className="text-danger fs-7s" role="alert">Contenu requis</small>}
                                    </div>
                                </div>
                            </div>

                            {/* Preview */}
                            <div className=' col-md-6 col-12'>
                                <div className=' mt-md-0 mt-4' style={{maxHeight: "90vh", overflowY: "auto"}}>
                                    <h5 className=' fs-6 fw-bold'>Prévisualisation</h5>
                                    {/* Hero */}
                                    <div className='bgcolor-hero-actuality mb-1'>
                                        <div className='container-xxl h-100'>
                                            <div className=' d-flex flex-column justify-content-end align-items-center text-center h-100 w-100 mx-auto' style={{maxWidth:'768px'}}>
                                                <h2 className=' fs-3 text-uppercase fw-bold'>{title}</h2>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='mx-auto' style={{maxWidth:'768px'}}>
                        
                                        <div className='d-flex flex-grow-0 justify-content-end'>
                                            <span className=' px-2 py-0 mb-1 bgcolor-secondary text-truncate-1 fs-8s w-auto'>{categoriesData?.data?.find(cat => cat.id ===  parseInt(category))?.name}</span>
                                        </div>
                                        <div className='tiptap' dangerouslySetInnerHTML={{__html: content}}></div>
                        
                                        <hr/>
                        
                                        <div className=' d-flex justify-content-end'>
                                            <span className='fs-9s'>{format(new Date(singleActuality.data?.createdAt), "dd MMM yyyy")}</span>
                                        </div>
                        
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button disabled={updateActuality.isPending} type='submit' className='d-flex align-items-center px-2 py-1 me-2 mt-3 mb-2 bgcolor-primary rounded-2'>
                            <i className="fa-light fa-paper-plane-top me-2 text-white"></i>
                            <span className=' fs-9s text-white'>Modifier</span>
                        </button>
                    </>
                }
            </form>
        </>
    )
}

export default ActualityEdit